import type { ChatSeedPreSubmitResult } from '../../chat-seed/types';

/**
 * Bridge between the dataset page (which owns the model that knows whether the
 * dataset exists server-side) and the chat-seed pre-submit (which runs when the
 * user sends the first message of a seeded new-dataset chat). Registered by the
 * page while its model is mounted, so pre-submit can create the dataset through
 * the same idempotent path the empty-state uses and the page updates in place.
 */
type DatasetSeedRuntime = {
  isPersisted: () => boolean;
  /**
   * Creates the dataset under its already-open id if it does not exist yet.
   * Idempotent: creating one that already exists returns it untouched.
   */
  persist: (params: { folderId?: string }) => Promise<ChatSeedPreSubmitResult>;
};

const runtimesByDatasetId = new Map<string, DatasetSeedRuntime>();

export function registerDatasetSeedRuntime(
  datasetId: string,
  runtime: DatasetSeedRuntime
): () => void {
  runtimesByDatasetId.set(datasetId, runtime);
  return () => {
    if (runtimesByDatasetId.get(datasetId) === runtime) {
      runtimesByDatasetId.delete(datasetId);
    }
  };
}

export function getDatasetSeedRuntime(
  datasetId: string
): DatasetSeedRuntime | undefined {
  return runtimesByDatasetId.get(datasetId);
}

export type { DatasetSeedRuntime };
